// GENERADO: editar packages/backend/src/shared/pricer-datec-motor.ts y ejecutar node scripts/sync-pricer-datec-motor.mjs
// Motor único del Pricer Datec (F3 · T2). TypeScript puro: SÓLO importa decimal.js.
// Lo copia el frontend con `node scripts/sync-pricer-datec-motor.mjs` (no editar la copia).
//
// Qué es: la fórmula del precio escrita UNA vez, con Decimal, que usan la pantalla y el servidor.
// Qué NO hace: no importa ninguna constante. Las REGLAS (tipos de compra, intereses de garantía y
// regla de redondeo) entran como DATO (`CatalogoMotor`); el servidor las arma y las congela dentro del
// payload (C1). La tarifa de FLETE, en cambio, se congela POR ÍTEM en `calculos[id].tarifa` (C11): un
// ítem editado toma la propuesta vigente y los demás conservan la suya, también en un recálculo global.
//
// Reglas de dinero (plan F3, D2, D8, D10):
//  * El motor trabaja en Decimal y devuelve números JS (`.toNumber()`). Un double hace ida y vuelta
//    exacta por JSON/jsonb/double precision para montos de 2 decimales.
//  * El redondeo vive en TRES funciones y nada más: `aFacturado` (montos que se facturan; identidad
//    con la regla NINGUNO), `aCentavos` (repartos) y `margenOperativoPct` (que es `number` a propósito:
//    es lo que miran las reglas de aprobación). Un test impide otro redondeo.
//  * Quién firma se decide con el precio EXACTO (D8/C2): `precioSinIVA`, `totalSinIVA`, `margenEfectivo`,
//    `margenOpAprox` y `margenControlado` salen del precio intención, con la regla que sea.
import Decimal from 'decimal.js';

/** Clon propio: no hereda la configuración de ningún otro módulo (D2). */
const D = Decimal.clone({ precision: 30, rounding: Decimal.ROUND_HALF_UP });
type Dec = InstanceType<typeof D>;

// ============================================================================
// TIPOS
// ============================================================================

export interface ReglaRedondeo {
  modo: 'NINGUNO' | 'CENTAVOS';
  decimales: number;
}

export interface TipoCompraConfig {
  label?: string;
  short?: string;
  color?: string;
  /** Sólo de presentación («No aplica» del almacén en la pantalla): el motor no la lee. */
  categoria?: 'HARDWARE' | 'SOFTWARE' | 'SERVICIO';
  aplicaFlete: boolean;
  aplicaNacionalizacion: boolean;
  aplicaGarantia: boolean;
  aplicaObsolescencia: boolean;
  aplicaPrevExtra: boolean;
  aplicaMkt: boolean;
  aplicaComisiones: boolean;
  aplicaIVA: boolean;
  esCompraExterior: boolean;
  pagoUSAdiferencial?: boolean;
  retencionGrupo: 'LOCAL' | 'EXTERIOR' | null;
  modoRetencion: 'DESCUENTO' | 'GROSS_UP' | 'NINGUNO';
}

export interface CatalogoMotor {
  motor: 2;
  tiposCompra: Record<string, TipoCompraConfig>;
  /**
   * Tabla INFORMATIVA (rótulos y propuesta al capturar): `pct` es fracción (0.05 = 5 %) o `null` si
   * no hay propuesta (C9). No se usa para recalcular ítems existentes: cada ítem trae su `tarifa`.
   */
  tiposFlete: Record<string, { label?: string; pct: number | null }>;
  tiposGarantiaBancaria: Record<string, { label?: string; interes: number; comMinBOB: number }>;
  redondeo: ReglaRedondeo;
  /**
   * `HEREDADA_V1`: las tasas de la garantía bancaria del tipo de la cotización no son las de hoy sino las
   * que se sacaron de lo que guardó una cotización anterior a F3 (`tasasDeBoletaGuardadas`). Una cotización
   * conserva las tarifas con las que se calculó; la marca dice que hay un número por aceptar contra las de
   * hoy. La borra la orden explícita de actualizar las reglas.
   */
  origenTasasGarantia?: 'HEREDADA_V1';
  /** ISO. Fuera de la huella. */
  capturadoEn: string;
}

export interface PricerItem {
  id: number;
  /**
   * Identidad ESTABLE del ítem (T5b): la asigna el servidor y no se reutiliza al borrar y agregar. El `id`
   * es un número de posición que la pantalla puede repetir; el `uid` no. Es la clave de la tarifa por
   * ítem y de las imágenes (F4). Opcional: las v1 guardadas no lo traen hasta el primer guardado.
   */
  uid?: string;
  num: number;
  lob: string;
  cebe: string;
  marcaProducto: string;
  numParte: string;
  descripcion: string;
  tipoCompra: string;
  cantidad: number;
  costoUnitario: number;
  pctDescuento: number;
  tipoFlete: string;
  /** Si está definido (no null/undefined) reemplaza el % del tipo de flete. */
  pctFleteOverride?: number | null;
  pctNacionalizacion: number;
  markup: number;
  pctGarantia: number;
  aniosGarantia: number;
}

export interface PricerParams {
  cliente: string;
  creadoPor: string;
  fecha: string;
  validezDias: number;
  tipoQuote: string;
  tcOficial: number;
  factorIVA: number;
  pctObsolescencia: number;
  pctPrevExtra: number;
  pctMkt: number;
  pctComisiones: number;
  retencionLocal: number;
  retencionExterior: number;
  moneda?: 'USD' | 'BOB';
  tcPresentacion?: number | null;
  factorUSA?: number;
  factorBO1?: number;
  factorBO2?: number;
  tipoGarantia: string;
}

export interface GarantiaItem {
  id: string;
  label: string;
  pct: number;
  dias: number;
  activa: boolean;
}

/** La tarifa de flete con la que se calculó un ítem. La escribe sólo el servidor (D5). */
export interface TarifaItem {
  /** Fracción aplicada; 0 si el tipo de compra no lleva flete. */
  pctFlete: number;
  /**
   * De dónde salió: `NO_APLICA` (el tipo de compra no lleva flete), `MANUAL` (la persona lo escribió,
   * también un 0 a propósito), `TIPO` (la propuesta vigente del tipo de flete, aunque valga 0),
   * `SIN_PROPUESTA` (lleva flete, sin override y sin propuesta: la persona lo tiene que escribir, C9)
   * o `HEREDADA_V1` (lo que traía una cotización anterior a F3).
   */
  origenFlete: 'NO_APLICA' | 'MANUAL' | 'TIPO' | 'SIN_PROPUESTA' | 'HEREDADA_V1';
}

export interface ItemCalculo {
  costoCDesc: number;
  montoFlete: number;
  cif: number;
  montoNac: number;
  montoRetencion: number;
  costoAlm: number;
  precioConMarkup: number;
  montoMarkup: number;
  prevGarantia: number;
  prevObs: number;
  prevExtra: number;
  prevMkt: number;
  prevCom: number;
  totalPrev: number;
  precioSinIVA: number;
  precioCIVA: number;
  cargaIVA: number;
  totalFacturado: number;
  totalCostoAlm: number;
  totalMarkup: number;
  totalPrevisiones: number;
  margenEfectivo: number;
  margenOpAprox: number;
  /** El margen que miran las reglas (= margenOpAprox desde F0). */
  margenControlado: number;
  /** Lo que le toca de la boleta (suma de las partes = boleta total). */
  boleta: number;
  /** La línea con su parte de la boleta (= totalFacturado + boleta). Fuente del documento al cliente. */
  totalConBoleta: number;
  /**
   * Precio unitario con la boleta adentro (pedido 9 de F4). Con CENTAVOS es `precioCIVA` + la boleta
   * redondeada POR UNIDAD (2 decimales) y `precioUnitarioConBoleta × cantidad` da `totalConBoleta`. Con
   * NINGUNO (y en una v1) es `precioCIVA + boleta / cantidad`, derivado: ver `unitarioConBoletaDerivado`.
   */
  precioUnitarioConBoleta: number;
  /** `true` si `precioUnitarioConBoleta` se derivó (NINGUNO o v1) en vez de salir del reparto por unidad. */
  unitarioConBoletaDerivado?: boolean;
  /**
   * Sólo al LEER una v1: `true` si la boleta de la línea no estaba guardada y `leerSnapshot` la repartió. El
   * documento de una v1 usa entonces «la cuenta de antes» (`pricer-datec-documento-v1.ts`) para imprimir lo mismo que ya recibió
   * el cliente. Nunca se guarda.
   */
  boletaRepartidaAlLeer?: boolean;
  /**
   * Sólo con CENTAVOS y boleta en la línea: `true` si la boleta se repartió por unidad; `false` si la
   * línea pasó el tope (`TOPE_BOLETA_POR_UNIDAD_USD`) y lleva su boleta exacta (ahí el unitario va SIN la boleta, a
   * 2 decimales, y la boleta exacta va en un renglón aparte «Costo financiero de garantía»: decisión 6a, confirmada
   * por Sebastián el 14-oct).
   */
  boletaPorUnidad?: boolean;
  /** El % de flete aplicado a este ítem: un solo origen del flete (C11, D7). */
  tarifa: TarifaItem;
}

export interface BoletaDetalle extends GarantiaItem {
  montoEfectivo: number;
  costoCalc: number;
  costoFinal: number;
  aplicaMinimo: boolean;
}

export interface BoletaCalculo {
  /** Lo que cuesta la boleta (suma de los costos finales de cada garantía). El cálculo exacto. */
  totalCosto: number;
  /**
   * Lo que se carga en el precio: Σ `calculos[id].boleta`. Con NINGUNO es `totalCosto`; con CENTAVOS
   * (boleta por unidad) puede diferir hasta medio centavo por unidad. Una v1 no lo trae: `leerSnapshot`
   * lo completa con `totalCosto`.
   */
  totalCargado?: number;
  pctSobreTotal: number;
  detalles: BoletaDetalle[];
  tipoGarantia: string;
  comMinUSD: number;
}

export interface Subtotales {
  totalCompraExt: number;
  totalCompraLoc: number;
  totalCompra: number;
  totalCostoAlm: number;
  totalMkup: number;
  totalPrev: number;
  totalSinIVA: number;
  totalFact: number;
  margenGlobal: number;
  hayCompraExterior: boolean;
}

/** Lo que se guarda en el payload desde F3. */
export interface SnapshotV2 {
  schemaVersion: 2;
  params: PricerParams;
  items: PricerItem[];
  garantias: GarantiaItem[];
  calculos: Record<number, ItemCalculo>;
  subtotales: Subtotales;
  boletaCalc: BoletaCalculo;
  totalConBoleta: number;
  catalogo: CatalogoMotor;
  /**
   * `true`: la cotización estaba con el cálculo pendiente de aceptar (el total guardado no coincidía con sus
   * reglas) y se guardó sin la orden explícita de aceptar. Un cambio de ida y vuelta no la resuelve: sólo
   * `actualizar.reglas` borra la marca.
   */
  aceptacionPendiente?: true;
  /** El total guardado ANTES de que esta marca se pusiera (I17): lo que la banda muestra como «de». */
  totalAntesDeAceptar?: number;
}

/** Lo que la pantalla, el PDF y el Excel leen: v1 adaptada o v2. */
export interface SnapshotVista {
  params: PricerParams;
  items: PricerItem[];
  garantias: GarantiaItem[];
  calculos: Record<number, ItemCalculo>;
  subtotales: Subtotales;
  boletaCalc: BoletaCalculo;
  totalConBoleta: number;
  /** Sólo las v2 lo traen. */
  catalogo: CatalogoMotor | null;
  /** Ver `SnapshotV2.aceptacionPendiente`. */
  aceptacionPendiente?: true;
  /** Ver `SnapshotV2.totalAntesDeAceptar`. */
  totalAntesDeAceptar?: number;
  /** Se conserva tal cual si la v1 lo traía; las v2 no lo traen. */
  pagosQuote?: unknown;
}

export interface EntradasNormalizadas {
  /** Entradas que mueven el precio de TODOS los ítems (IVA, previsiones, retenciones). */
  globalesDeItem: Record<string, number>;
  /** Entradas que sólo mueven la boleta (tipo de cambio y tipo de garantía). */
  boleta: { tcOficial: number; tipoGarantia: string };
  items: Record<number, Record<string, number | string | null>>;
  garantias: Record<string, { pct: number; dias: number; activa: boolean }>;
  /** Cuántas garantías vinieron: dos con el mismo `id` esconden un cambio de boleta (I2). */
  nGarantias: number;
  /** El `uid` de los ítems que lo traen. Un `uid` distinto en el mismo `id` es OTRO ítem (I1). */
  uids: Record<number, string>;
}

/** Lo que el servidor sabe de la propuesta de flete de hoy, por tipo de flete (C9: `null` = sin muestra). */
export type FleteVigente = Record<string, { pct: number | null }>;

// ============================================================================
// HELPERS NUMÉRICOS
// ============================================================================

/** Número tolerante: texto, null, NaN e infinitos dan 0 (igual que el motor de siempre). */
function num(v: unknown): number {
  if (typeof v === 'number') return Number.isFinite(v) ? v : 0;
  const parsed = parseFloat(String(v ?? ''));
  return Number.isNaN(parsed) || !Number.isFinite(parsed) ? 0 : parsed;
}

const dec = (v: unknown): Dec => new D(num(v));
const fracDe = (v: unknown): Dec => dec(v).div(100);
const aNumero = (d: Dec): number => d.toNumber();
const CERO = new D(0);
const UNO = new D(1);

/**
 * Tope de la boleta por unidad (C15): si en una línea la diferencia entre lo cargado por boleta (redondeada
 * por unidad) y su parte exacta supera esto, en valor absoluto, esa línea NO reparte por unidad y lleva su
 * boleta exacta. Constante de código, en dólares.
 */
export const TOPE_BOLETA_POR_UNIDAD_USD = 1;

/** Redondeo de REPARTOS (la boleta por línea): siempre centavos, también con la regla NINGUNO. */
const DECIMALES_REPARTO = 2;

/** Único punto de redondeo de los repartos y de la regla CENTAVOS. */
function aCentavos(d: Dec, decimales: number = DECIMALES_REPARTO): Dec {
  return d.toDecimalPlaces(decimales, D.ROUND_HALF_UP);
}

/**
 * Half-up de Decimal a 2 decimales, como número. La usan el modelo del documento, el HTML, el Excel y la
 * pantalla (pedido 3 de F4): reemplaza a `Math.round(x * 100) / 100`, que se equivoca en `1.005`.
 */
export function aDosDecimales(n: number): number {
  return aNumero(aCentavos(dec(n)));
}

/** Único punto por donde pasa todo monto que se FACTURA. Con NINGUNO es la identidad. */
function aFacturado(d: Dec, regla: ReglaRedondeo): Dec {
  return regla.modo === 'CENTAVOS' ? aCentavos(d, regla.decimales) : d;
}

const REGLA_NINGUNA: ReglaRedondeo = { modo: 'NINGUNO', decimales: 2 };

// ============================================================================
// MARGEN OPERATIVO (number, idéntico al de siempre)
// ============================================================================

/**
 * El margen operativo del Pricer, en %, tal como lo miran las reglas de aprobación (OBS-013/014):
 * es el MARKUP, (P. sin IVA − previsiones − costo almacén) / (P. sin IVA − previsiones).
 * Redondeado a 2 decimales: el umbral («< 20 %») se compara contra el número que ve la gente.
 * Sin base devuelve 0. Queda en `number` a propósito: `Math.round` no es igual al half-up de Decimal
 * (difiere en negativos y en empates) y las reglas ya firmaron con esta cuenta.
 */
export function margenOperativoPct(sinIVA: number, previsiones: number, costo: number): number {
  const base = sinIVA - previsiones;
  if (!(base > 0)) return 0;
  return Math.round(((base - costo) / base) * 10000) / 100;
}

/**
 * El margen con el que se decide el TRAMO de aprobación (I9). Es el de `margenOperativoPct` pero con la razón
 * llevada a 12 dígitos significativos ANTES de redondear a 2 decimales: el aviso previo calcula con el
 * snapshot recién armado (doubles de 17 dígitos) y el envío con el guardado (Prisma los devuelve a 16), y
 * en el filo de un tramo (un markup de 19,995) el ruido del dígito 16 los mandaba a lados distintos. Con
 * esta función los dos dan el mismo número. El redondeo es de Decimal, «mitad hacia arriba» (T5c, decisión
 * provisional del 14-oct): 7,995 % -> 8,00 % y 19,995 % -> 20,00 % (en coma flotante 7,995 bajaba a 7,99). Es la ÚNICA que miran las reglas.
 */
export function margenParaFirma(sinIVA: number, previsiones: number, costo: number): number {
  const base = sinIVA - previsiones;
  if (!(base > 0)) return 0;
  return pctParaFirma((base - costo) / base);
}

/** Las tres formas de medir el margen que puede usar una regla de aprobación (F6 T16, OBS-014). */
export type MetricaDeMargenMotor = 'MARKUP' | 'MARGEN_DESPUES_DE_PREVISIONES' | 'MARGEN_BRUTO';

/**
 * Margen DESPUÉS DE PREVISIONES, en %: (P. sin IVA − previsiones − costo) / P. sin IVA. Misma cuenta de firma que
 * `margenParaFirma` (12 dígitos significativos, mitad hacia arriba): lo que mira la regla en el aviso previo, el
 * envío y la reapertura es EL MISMO número. Sin base, 0.
 */
export function margenDespuesDePrevisionesParaFirma(
  sinIVA: number,
  previsiones: number,
  costo: number
): number {
  if (!(sinIVA > 0)) return 0;
  return pctParaFirma((sinIVA - previsiones - costo) / sinIVA);
}

/** Margen BRUTO, en %: (P. sin IVA − costo) / P. sin IVA; no descuenta previsiones. Misma cuenta de firma. */
export function margenBrutoParaFirma(sinIVA: number, costo: number): number {
  if (!(sinIVA > 0)) return 0;
  return pctParaFirma((sinIVA - costo) / sinIVA);
}

/** Las tres métricas de un mismo conjunto de montos (una línea o el total del documento). */
export function margenesParaFirma(
  sinIVA: number,
  previsiones: number,
  costo: number
): Record<MetricaDeMargenMotor, number> {
  return {
    MARKUP: margenParaFirma(sinIVA, previsiones, costo),
    MARGEN_DESPUES_DE_PREVISIONES: margenDespuesDePrevisionesParaFirma(sinIVA, previsiones, costo),
    MARGEN_BRUTO: margenBrutoParaFirma(sinIVA, costo),
  };
}

/**
 * El margen (fracción -> %) a 2 decimales con la cuenta que decide el tramo: 12 dígitos significativos y «mitad hacia
 * arriba» (6c). Es la ÚNICA cuenta del margen que se muestra y que se firma (A1, T7b): la pantalla la usa en `fmtMargenOp`.
 */
export function pctParaFirma(fraccion: number): number {
  if (!Number.isFinite(fraccion)) return 0;
  const razon = new D(fraccion.toPrecision(12));
  // `+ 0` normaliza el −0 (−0,005 % queda en 0, no en −0,00).
  return aNumero(razon.times(100).toDecimalPlaces(2, D.ROUND_HALF_CEIL)) + 0;
}

/**
 * Las tasas de la garantía bancaria con las que se calculó una cotización, sacadas de LO GUARDADO (B3 de la
 * ronda T5b): el interés anual (`costoCalc × 360 / días / monto`, a 6 dígitos significativos) y la comisión
 * mínima en Bs (`comMinUSD × tcOficial`, a 2 decimales). Se usa `costoCalc` y no `costoFinal`, así que el
 * interés sale aunque se haya aplicado la comisión mínima. `null` si no hay garantía, si no hay un detalle
 * con días y monto, o si los detalles no coinciden entre sí (no se inventa una tasa).
 */
export function tasasDeBoletaGuardadas(
  s: Pick<SnapshotVista, 'params' | 'boletaCalc'>
): { tipo: string; interes: number; comMinBOB: number } | null {
  const b = s?.boletaCalc;
  const tipo = b?.tipoGarantia;
  if (!b || !tipo || tipo === 'Ninguno') return null;
  const tc = dec(s.params?.tcOficial);
  if (!tc.gt(0) || !(num(b.comMinUSD) > 0)) return null;
  const intereses: Dec[] = [];
  for (const d of b.detalles ?? []) {
    if (d.activa === false || !(num(d.dias) > 0) || !(num(d.montoEfectivo) > 0)) continue;
    intereses.push(
      dec(d.costoCalc).times(360).div(dec(d.dias)).div(dec(d.montoEfectivo)).toSignificantDigits(6)
    );
  }
  if (intereses.length === 0 || intereses.some((i) => !i.eq(intereses[0]!))) return null;
  return {
    tipo,
    interes: aNumero(intereses[0]!),
    comMinBOB: aNumero(dec(b.comMinUSD).times(tc).toDecimalPlaces(2, D.ROUND_HALF_UP)),
  };
}

/** El mismo margen en fracción, sin redondear. Sin base devuelve 0. */
export function margenOperativo(sinIVA: number, previsiones: number, costo: number): number {
  const base = sinIVA - previsiones;
  return base > 0 ? (base - costo) / base : 0;
}

// ============================================================================
// HUELLA DEL CATÁLOGO
// ============================================================================

function jsonEstable(v: unknown): string {
  if (Array.isArray(v)) return `[${v.map(jsonEstable).join(',')}]`;
  if (v && typeof v === 'object') {
    const o = v as Record<string, unknown>;
    return `{${Object.keys(o)
      .sort()
      .map((k) => `${JSON.stringify(k)}:${jsonEstable(o[k])}`)
      .join(',')}}`;
  }
  return JSON.stringify(v);
}

/** FNV-1a de 32 bits sobre un texto (unidades UTF-16). */
function fnv1a(texto: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < texto.length; i++) {
    h ^= texto.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(16).padStart(8, '0');
}

/** Huella del catálogo ENTERO, sin `capturadoEn`. Mezcla reglas con la tabla informativa de flete: para
 *  decidir si «cambiaron las reglas» se usa `huellaDeReglas`. */
export function huellaDeCatalogo(c: CatalogoMotor): string {
  const { capturadoEn: _fueraDeLaHuella, ...resto } = c;
  void _fueraDeLaHuella;
  return fnv1a(jsonEstable(resto));
}

/**
 * Huella de las REGLAS que recalculan una cotización: tipos de compra, tasas de garantía bancaria y regla
 * de redondeo (I3). La tabla informativa de flete NO entra: una v1 (flete en 0) y la respuesta de
 * `GET /catalog` (flete en `null`) daban huellas distintas sin que nada hubiera cambiado.
 */
export function huellaDeReglas(c: CatalogoMotor): string {
  return fnv1a(
    jsonEstable({
      tiposCompra: c.tiposCompra,
      tiposGarantiaBancaria: c.tiposGarantiaBancaria,
      redondeo: c.redondeo,
    })
  );
}

/** Huella de la propuesta de flete vigente (por tipo de flete; `pct: null` = sin propuesta). */
export function huellaDeFlete(vigente: FleteVigente): string {
  const solo: Record<string, number | null> = {};
  for (const [tipo, v] of Object.entries(vigente)) solo[tipo] = v?.pct ?? null;
  return fnv1a(jsonEstable(solo));
}

// ============================================================================
// CÁLCULO POR ÍTEM
// ============================================================================

type ItemSinBoleta = Omit<
  ItemCalculo,
  | 'boleta'
  | 'totalConBoleta'
  | 'precioUnitarioConBoleta'
  | 'unitarioConBoletaDerivado'
  | 'boletaRepartidaAlLeer'
  | 'boletaPorUnidad'
  | 'tarifa'
>;

function tipoDe(item: PricerItem, c: CatalogoMotor): TipoCompraConfig {
  return c.tiposCompra[item.tipoCompra] ?? c.tiposCompra.HW_EXT!;
}

/**
 * La tarifa de flete de un ítem que TOMA la vigente (nuevo, o editado en algo que la determina, C11):
 * sin llevar flete el tipo de compra, `NO_APLICA`; con override (aunque sea 0 %), `MANUAL`; si no, la
 * propuesta vigente del tipo de flete (`fleteVigente`, que manda aun cuando sea `null`; o la tabla
 * informativa del catálogo si no se pasó). Una propuesta `null` es `SIN_PROPUESTA` con 0: distinto de
 * una propuesta que vale 0 (`TIPO`) y de un 0 escrito a mano (`MANUAL`).
 */
export function tarifaDeItem(
  item: PricerItem,
  c: CatalogoMotor,
  fleteVigente?: FleteVigente
): TarifaItem {
  if (!tipoDe(item, c).aplicaFlete) return { pctFlete: 0, origenFlete: 'NO_APLICA' };
  if (item.pctFleteOverride != null) {
    return { pctFlete: aNumero(fracDe(item.pctFleteOverride)), origenFlete: 'MANUAL' };
  }
  const propuesta = propuestaDelTipo(item.tipoFlete, c, fleteVigente);
  if (propuesta === null) return { pctFlete: 0, origenFlete: 'SIN_PROPUESTA' };
  return { pctFlete: aNumero(dec(propuesta ?? 0)), origenFlete: 'TIPO' };
}

/** La propuesta del tipo de flete: `null` = no hay; `undefined` = el tipo no está en ninguna tabla (0). */
function propuestaDelTipo(
  tipoFlete: string,
  c: CatalogoMotor,
  fleteVigente?: FleteVigente
): number | null | undefined {
  const vigente = fleteVigente?.[tipoFlete];
  if (vigente && vigente.pct !== undefined) return vigente.pct;
  return c.tiposFlete[tipoFlete]?.pct;
}

/**
 * ¿Cambió en el ítem algo que DETERMINA su tarifa de flete? Sólo tres cosas: el tipo de compra (decide
 * si lleva flete), el tipo de flete y el override. Cantidad, costo, margen, descuento, nacionalización,
 * garantía y descripción no la tocan (definición 2 del 13-oct).
 */
export function cambiaLaTarifa(antes: PricerItem, despues: PricerItem): boolean {
  const ov = (i: PricerItem) => (i.pctFleteOverride == null ? null : num(i.pctFleteOverride));
  return (
    String(antes.tipoCompra) !== String(despues.tipoCompra) ||
    String(antes.tipoFlete) !== String(despues.tipoFlete) ||
    ov(antes) !== ov(despues)
  );
}

/**
 * La tarifa de un cálculo ya guardado sin `tarifa` (una v1): el flete escrito a mano si el ítem lo tiene
 * (`pctFleteOverride`: es lo que la persona escribió, aunque el costo sea 0 y no se haya cobrado nada), y si
 * no, lo que se aplicó, `montoFlete / costoCDesc`.
 */
function tarifaHeredada(
  calc: { montoFlete?: number; costoCDesc?: number },
  item?: Pick<PricerItem, 'pctFleteOverride'>
): TarifaItem {
  if (item?.pctFleteOverride != null) {
    return { pctFlete: aNumero(fracDe(item.pctFleteOverride)), origenFlete: 'MANUAL' };
  }
  const costo = dec(calc.costoCDesc);
  const pct = costo.gt(0) ? dec(calc.montoFlete).div(costo) : CERO;
  return { pctFlete: aNumero(pct), origenFlete: 'HEREDADA_V1' };
}

function calcularItemBase(
  item: PricerItem,
  params: PricerParams,
  c: CatalogoMotor,
  tarifa: TarifaItem
): ItemSinBoleta {
  const tipo = tipoDe(item, c);
  const cant = dec(item.cantidad);
  const costoU = dec(item.costoUnitario);
  const fracDesc = fracDe(item.pctDescuento);
  const fletePct = tipo.aplicaFlete ? dec(tarifa.pctFlete) : CERO;
  const fracNac = tipo.aplicaNacionalizacion ? fracDe(item.pctNacionalizacion) : CERO;
  const fracMarkup = fracDe(item.markup);
  const fracGarantia = tipo.aplicaGarantia ? fracDe(item.pctGarantia) : CERO;
  const aniosGar = dec(item.aniosGarantia);

  const costoCDesc = costoU.times(UNO.minus(fracDesc));
  const montoFlete = costoCDesc.times(fletePct);
  const cif = costoCDesc.plus(montoFlete);
  const montoNac = cif.times(fracNac);
  const costoAlmBase = cif.plus(montoNac);
  const fracRetencion =
    tipo.retencionGrupo === 'LOCAL'
      ? fracDe(params.retencionLocal)
      : tipo.retencionGrupo === 'EXTERIOR'
        ? fracDe(params.retencionExterior)
        : CERO;
  const costoAlm =
    tipo.modoRetencion === 'DESCUENTO'
      ? costoAlmBase.times(UNO.minus(fracRetencion))
      : tipo.modoRetencion === 'GROSS_UP' && fracRetencion.lt(1)
        ? costoAlmBase.div(UNO.minus(fracRetencion))
        : costoAlmBase;
  const montoRetencion = costoAlm.minus(costoAlmBase);
  const precioConMarkup = fracMarkup.lt(1) ? costoAlm.div(UNO.minus(fracMarkup)) : costoAlm;
  const montoMarkup = precioConMarkup.minus(costoAlm);
  const prevGarantia = tipo.aplicaGarantia
    ? precioConMarkup.times(fracGarantia).times(aniosGar)
    : CERO;
  const prevObs = tipo.aplicaObsolescencia
    ? precioConMarkup.times(fracDe(params.pctObsolescencia))
    : CERO;
  const prevExtra = tipo.aplicaPrevExtra
    ? precioConMarkup.times(fracDe(params.pctPrevExtra))
    : CERO;
  const prevMkt = tipo.aplicaMkt ? precioConMarkup.times(fracDe(params.pctMkt)) : CERO;
  const prevCom = tipo.aplicaComisiones
    ? precioConMarkup.times(fracDe(params.pctComisiones))
    : CERO;
  const totalPrev = prevGarantia.plus(prevObs).plus(prevExtra).plus(prevMkt).plus(prevCom);
  // D8: el precio INTENCIÓN (sin redondear) es el que miran las reglas de firma.
  const precioSinIVA = precioConMarkup.plus(totalPrev);
  const fracIVA = fracDe(params.factorIVA); // p.ej. 0.84
  const precioCIVAExacto =
    tipo.aplicaIVA && fracIVA.gt(0) ? precioSinIVA.div(fracIVA) : precioSinIVA;
  // Lo que se factura pasa por `aFacturado`: precio unitario y total de la línea.
  const precioCIVA = aFacturado(precioCIVAExacto, c.redondeo);
  const cargaIVA = precioCIVA.minus(precioSinIVA);

  const totalFacturado = aFacturado(precioCIVA.times(cant), c.redondeo);
  const totalCostoAlm = costoAlm.times(cant);
  const totalMarkup = montoMarkup.times(cant);
  const totalPrevisiones = totalPrev.times(cant);
  const margenEfectivo = precioSinIVA.gt(0) ? precioSinIVA.minus(costoAlm).div(precioSinIVA) : CERO;
  // Margen Op. = el MARKUP: (P. sin IVA − Previsiones − Costo Almacén) / (P. sin IVA − Previsiones).
  const baseOp = precioSinIVA.minus(totalPrev);
  const margenOpAprox = baseOp.gt(0) ? baseOp.minus(costoAlm).div(baseOp) : CERO;

  return {
    costoCDesc: aNumero(costoCDesc),
    montoFlete: aNumero(montoFlete),
    cif: aNumero(cif),
    montoNac: aNumero(montoNac),
    montoRetencion: aNumero(montoRetencion),
    costoAlm: aNumero(costoAlm),
    precioConMarkup: aNumero(precioConMarkup),
    montoMarkup: aNumero(montoMarkup),
    prevGarantia: aNumero(prevGarantia),
    prevObs: aNumero(prevObs),
    prevExtra: aNumero(prevExtra),
    prevMkt: aNumero(prevMkt),
    prevCom: aNumero(prevCom),
    totalPrev: aNumero(totalPrev),
    precioSinIVA: aNumero(precioSinIVA),
    precioCIVA: aNumero(precioCIVA),
    cargaIVA: aNumero(cargaIVA),
    totalFacturado: aNumero(totalFacturado),
    totalCostoAlm: aNumero(totalCostoAlm),
    totalMarkup: aNumero(totalMarkup),
    totalPrevisiones: aNumero(totalPrevisiones),
    margenEfectivo: aNumero(margenEfectivo),
    margenOpAprox: aNumero(margenOpAprox),
    margenControlado: aNumero(margenOpAprox),
  };
}

/**
 * El cálculo de un ítem. La parte de la boleta (`boleta`, `totalConBoleta`) depende del total de la
 * cotización: acá queda en 0 y la completa `construirSnapshot` (o `leerSnapshot`) con el reparto.
 * `opciones.tarifa` fija la tarifa de flete del ítem (un recálculo global la conserva); si no viene,
 * se toma la vigente (`tarifaDeItem`).
 */
export function calcularItem(
  item: PricerItem,
  params: PricerParams,
  c: CatalogoMotor,
  opciones?: { tarifa?: TarifaItem; fleteVigente?: FleteVigente }
): ItemCalculo {
  const tarifa = opciones?.tarifa ?? tarifaDeItem(item, c, opciones?.fleteVigente);
  const base = calcularItemBase(item, params, c, tarifa);
  return {
    ...base,
    boleta: 0,
    totalConBoleta: base.totalFacturado,
    precioUnitarioConBoleta: base.precioCIVA,
    tarifa,
  };
}

// ============================================================================
// BOLETA / GARANTÍA BANCARIA
// ============================================================================

export function calcularBoleta(
  totalFacturado: number,
  garantias: GarantiaItem[],
  tipoGarantia: string,
  tcOficial: number,
  c: CatalogoMotor
): BoletaCalculo {
  const config = c.tiposGarantiaBancaria[tipoGarantia];
  if (!config || tipoGarantia === 'Ninguno') {
    return { totalCosto: 0, pctSobreTotal: 0, detalles: [], tipoGarantia, comMinUSD: 0 };
  }
  const total = dec(totalFacturado);
  const tc = dec(tcOficial);
  const comMinUSD = tc.gt(0) ? new D(config.comMinBOB).div(tc) : CERO;
  const detalles = garantias
    .filter((g) => g.activa)
    .map((g) => {
      const montoEfectivo = total.times(fracDe(g.pct));
      const costoCalc = new D(config.interes).div(360).times(dec(g.dias)).times(montoEfectivo);
      const aplicaMinimo = costoCalc.lt(comMinUSD);
      const costoFinal = aFacturado(aplicaMinimo ? comMinUSD : costoCalc, c.redondeo);
      return {
        ...g,
        montoEfectivo: aNumero(montoEfectivo),
        costoCalc: aNumero(costoCalc),
        costoFinal: aNumero(costoFinal),
        aplicaMinimo,
        // Se conserva el Decimal para sumar sin pasar por double.
        _costoFinalExacto: costoFinal,
      };
    });
  const totalCosto = detalles.reduce((s, d) => s.plus(d._costoFinalExacto), CERO);
  const pctSobreTotal = total.gt(0) ? totalCosto.div(total) : CERO;
  return {
    totalCosto: aNumero(totalCosto),
    pctSobreTotal: aNumero(pctSobreTotal),
    detalles: detalles.map(({ _costoFinalExacto, ...d }) => {
      void _costoFinalExacto;
      return d;
    }),
    tipoGarantia,
    comMinUSD: aNumero(comMinUSD),
  };
}

/**
 * Cuánto de la boleta le toca a cada ítem: a prorrata de lo facturado por línea (la garantía cubre
 * el contrato entero, no una línea). Cada parte va a centavos y el resto del redondeo a la línea de
 * mayor monto, para que la suma de las partes dé EXACTAMENTE la boleta total.
 */
function repartir(
  items: PricerItem[],
  calculos: Record<number, { totalFacturado: number }>,
  boletaTotal: number
): Record<number, number> {
  const out: Record<number, number> = {};
  const total0 = dec(boletaTotal);
  if (!total0.gt(0)) {
    for (const it of items) out[it.id] = 0;
    return out;
  }
  const montos = items.map((it) => ({
    id: it.id,
    monto: dec(calculos[it.id]?.totalFacturado ?? 0),
  }));
  const total = montos.reduce((s, x) => s.plus(x.monto), CERO);
  if (!total.gt(0)) {
    for (const it of items) out[it.id] = 0;
    return out;
  }
  let repartido = CERO;
  const partes = new Map<number, Dec>();
  for (const { id, monto } of montos) {
    const parte = aCentavos(total0.times(monto.div(total)));
    partes.set(id, parte);
    repartido = repartido.plus(parte);
  }
  const resto = aCentavos(total0.minus(repartido));
  if (!resto.isZero() && montos.length > 0) {
    const mayor = montos.reduce((a, b) => (b.monto.gt(a.monto) ? b : a));
    partes.set(mayor.id, aCentavos(partes.get(mayor.id)!.plus(resto)));
  }
  for (const [id, parte] of partes) out[id] = aNumero(parte);
  return out;
}

export function repartirBoleta(
  items: PricerItem[],
  calculos: Record<number, ItemCalculo>,
  boletaTotal: number,
  _c: CatalogoMotor
): Record<number, number> {
  void _c; // el reparto siempre es a centavos; la regla del catálogo ya actuó sobre cada costo
  return repartir(items, calculos, boletaTotal);
}

// ============================================================================
// SUBTOTALES
// ============================================================================

export function calcularSubtotales(
  items: PricerItem[],
  calculos: Record<number, ItemCalculo>,
  c: CatalogoMotor
): Subtotales {
  let totalCompraExt = CERO;
  let totalCompraLoc = CERO;
  let totalCostoAlm = CERO;
  let totalMkup = CERO;
  let totalPrev = CERO;
  let totalSinIVA = CERO;
  let totalFact = CERO;
  let hayCompraExterior = false;

  for (const it of items) {
    const calc = calculos[it.id];
    if (!calc) continue;
    const cant = dec(it.cantidad);
    const tipo = tipoDe(it, c);
    if (tipo.esCompraExterior) {
      totalCompraExt = totalCompraExt.plus(dec(calc.costoCDesc).times(cant));
      hayCompraExterior = true;
    } else {
      totalCompraLoc = totalCompraLoc.plus(dec(calc.costoCDesc).times(cant));
    }
    totalCostoAlm = totalCostoAlm.plus(dec(calc.totalCostoAlm));
    totalMkup = totalMkup.plus(dec(calc.totalMarkup));
    totalPrev = totalPrev.plus(dec(calc.totalPrevisiones));
    totalSinIVA = totalSinIVA.plus(dec(calc.precioSinIVA).times(cant));
    totalFact = totalFact.plus(dec(calc.totalFacturado));
  }

  const margenGlobal = totalSinIVA.gt(0) ? totalSinIVA.minus(totalCostoAlm).div(totalSinIVA) : CERO;
  return {
    totalCompraExt: aNumero(totalCompraExt),
    totalCompraLoc: aNumero(totalCompraLoc),
    totalCompra: aNumero(totalCompraExt.plus(totalCompraLoc)),
    totalCostoAlm: aNumero(totalCostoAlm),
    totalMkup: aNumero(totalMkup),
    totalPrev: aNumero(totalPrev),
    totalSinIVA: aNumero(totalSinIVA),
    totalFact: aNumero(totalFact),
    margenGlobal: aNumero(margenGlobal),
    hayCompraExterior,
  };
}

// ============================================================================
// ENTRADAS DE DINERO (qué cuenta como «cambió el precio»)
// ============================================================================

/**
 * La clasificación de TODA clave de las entradas (D4, M2/M3): cada una es de ítem, de boleta,
 * de garantía o no monetaria. Un test recorre las claves de `itemBase()`, `paramsBase()` y de una
 * garantía: una clave nueva sin clasificar es un rojo.
 *  - `item`: mueven el precio de ESE ítem.
 *  - `globalDeItem`: mueven el precio de TODOS los ítems.
 *  - `boleta` y `garantia`: sólo mueven la boleta (los ítems se reusan).
 */
export const CLASIFICACION_ENTRADAS = {
  item: [
    'tipoCompra',
    'cantidad',
    'costoUnitario',
    'pctDescuento',
    'tipoFlete',
    'pctFleteOverride',
    'pctNacionalizacion',
    'markup',
    'pctGarantia',
    'aniosGarantia',
  ],
  globalDeItem: [
    'factorIVA',
    'pctObsolescencia',
    'pctPrevExtra',
    'pctMkt',
    'pctComisiones',
    'retencionLocal',
    'retencionExterior',
  ],
  boleta: ['tcOficial', 'tipoGarantia'],
  garantia: ['pct', 'dias', 'activa'],
  noMonetarias: {
    item: ['id', 'uid', 'num', 'lob', 'cebe', 'marcaProducto', 'numParte', 'descripcion'],
    params: [
      'cliente',
      'creadoPor',
      'fecha',
      'validezDias',
      'tipoQuote',
      'moneda',
      'tcPresentacion',
      'factorUSA',
      'factorBO1',
      'factorBO2',
    ],
    garantia: ['id', 'label'],
  },
} as const;

/** Claves de ítem que son texto; el resto son números. */
const ITEM_TEXTO: readonly string[] = ['tipoCompra', 'tipoFlete'];

/**
 * Normaliza las entradas que mueven un precio igual que lo hace el motor al calcular: faltante
 * numérico = 0 (también una retención ausente, §5.c), `pctFleteOverride` nulo = ausente, números por
 * valor (no por texto). Lo NO monetario no entra. La usan el servidor (T5) y la pantalla (T7).
 */
export function entradasDeDinero(
  params: Partial<PricerParams> | null | undefined,
  items: PricerItem[] | null | undefined,
  garantias: GarantiaItem[] | null | undefined
): EntradasNormalizadas {
  const p = (params ?? {}) as Record<string, unknown>;
  const globalesDeItem: Record<string, number> = {};
  for (const k of CLASIFICACION_ENTRADAS.globalDeItem) globalesDeItem[k] = num15(p[k]);
  const boleta = {
    tcOficial: num15(p.tcOficial),
    tipoGarantia: typeof p.tipoGarantia === 'string' ? p.tipoGarantia : 'Ninguno',
  };

  const its: EntradasNormalizadas['items'] = {};
  const uids: Record<number, string> = {};
  for (const it of items ?? []) {
    const fila: Record<string, number | string | null> = {};
    const o = it as unknown as Record<string, unknown>;
    for (const k of CLASIFICACION_ENTRADAS.item) {
      if (k === 'pctFleteOverride') fila[k] = o[k] == null ? null : num15(o[k]);
      else if (ITEM_TEXTO.includes(k)) fila[k] = String(o[k]);
      else fila[k] = num15(o[k]);
    }
    its[it.id] = fila;
    if (typeof it.uid === 'string' && it.uid !== '') uids[it.id] = it.uid;
  }

  const gs: EntradasNormalizadas['garantias'] = {};
  for (const g of garantias ?? []) {
    gs[g.id] = { pct: num15(g.pct), dias: num15(g.dias), activa: g.activa === true };
  }
  return {
    globalesDeItem,
    boleta,
    items: its,
    garantias: gs,
    nGarantias: (garantias ?? []).length,
    uids,
  };
}

const mismoValor = (a: unknown, b: unknown) => jsonEstable(a) === jsonEstable(b);

/**
 * Un número de entrada a 15 dígitos significativos (I8): Prisma guarda los doubles con 16 y devuelve
 * `1217.075187468256` por `1217.0751874682558`; comparar por igualdad exacta veía un «cambio» donde no
 * lo hay. 15 dígitos absorbe ese ruido y distingue cualquier cifra que una persona pueda escribir.
 */
function num15(v: unknown): number {
  const n = num(v);
  return n === 0 ? 0 : Number(n.toPrecision(15));
}

export interface CambioDeDinero {
  cambio: boolean;
  /** Cambió una entrada que mueve el precio de TODOS los ítems. */
  globalesDeItem: boolean;
  /** Cambió algo de la boleta (tipo de cambio, tipo de garantía o garantías). */
  boleta: boolean;
  /** Cambió la boleta y NADA más: los ítems se reusan tal cual. */
  soloBoleta: boolean;
  itemsCambiados: number[];
  removidos: number[];
}

export function cambiaElDinero(a: EntradasNormalizadas, b: EntradasNormalizadas): CambioDeDinero {
  const globalesDeItem = !mismoValor(a.globalesDeItem, b.globalesDeItem);
  const itemsCambiados: number[] = [];
  for (const [id, entradas] of Object.entries(b.items)) {
    const antes = a.items[Number(id)];
    // Un `uid` distinto en el mismo `id` es OTRO ítem aunque traiga las mismas entradas (I1). Si falta de
    // un lado (una pantalla vieja que lo descarta, una v1) se sigue por `id`.
    const uidAntes = a.uids?.[Number(id)];
    const uidAhora = b.uids?.[Number(id)];
    const reemplazado = uidAntes !== undefined && uidAhora !== undefined && uidAntes !== uidAhora;
    if (!antes || reemplazado || !mismoValor(antes, entradas)) itemsCambiados.push(Number(id));
  }
  const removidos = Object.keys(a.items)
    .map(Number)
    .filter((id) => !(id in b.items));
  const boleta =
    !mismoValor(a.boleta, b.boleta) ||
    !mismoValor(a.garantias, b.garantias) ||
    a.nGarantias !== b.nGarantias;
  const deItems = globalesDeItem || itemsCambiados.length > 0 || removidos.length > 0;
  return {
    cambio: deItems || boleta,
    globalesDeItem,
    boleta,
    soloBoleta: boleta && !deItems,
    itemsCambiados,
    removidos,
  };
}

// ============================================================================
// SNAPSHOT
// ============================================================================

/** Lo que falta en un catálogo (las reglas) para poder calcular. */
export class CatalogoIncompletoError extends Error {
  readonly code = 'CATALOGO_INCOMPLETO';
  readonly faltantes: string[];
  constructor(faltantes: string[]) {
    super(`Catálogo de reglas incompleto, falta o es inválido: ${faltantes.join(', ')}`);
    this.name = 'CatalogoIncompletoError';
    this.faltantes = faltantes;
  }
}

const BANDERAS_TIPO_COMPRA = [
  'aplicaFlete',
  'aplicaNacionalizacion',
  'aplicaGarantia',
  'aplicaObsolescencia',
  'aplicaPrevExtra',
  'aplicaMkt',
  'aplicaComisiones',
  'aplicaIVA',
  'esCompraExterior',
] as const;

const esObjeto = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === 'object' && !Array.isArray(v);
const esFinito = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

/**
 * Comprueba que un catálogo congelado trae todo lo que el motor necesita, ANTES de calcular. Un
 * catálogo incompleto es una forma de no poder abrir ni guardar una cotización: se prefiere un error
 * propio y claro (`CatalogoIncompletoError`) a un `DecimalError` crudo o a un `NaN` silencioso (M7).
 */
export function validarCatalogo(c: unknown): asserts c is CatalogoMotor {
  const faltantes: string[] = [];
  const cat = esObjeto(c) ? c : {};
  const compras = cat.tiposCompra;
  if (!esObjeto(compras)) faltantes.push('tiposCompra');
  else {
    if (!esObjeto(compras.HW_EXT)) faltantes.push('tiposCompra.HW_EXT');
    for (const [clave, cfg] of Object.entries(compras)) {
      if (!esObjeto(cfg)) {
        faltantes.push(`tiposCompra.${clave}`);
        continue;
      }
      for (const b of BANDERAS_TIPO_COMPRA) {
        if (typeof cfg[b] !== 'boolean') faltantes.push(`tiposCompra.${clave}.${b}`);
      }
    }
  }
  const garantias = cat.tiposGarantiaBancaria;
  if (!esObjeto(garantias)) faltantes.push('tiposGarantiaBancaria');
  else {
    for (const [clave, cfg] of Object.entries(garantias)) {
      const o = esObjeto(cfg) ? cfg : {};
      if (!esFinito(o.interes)) faltantes.push(`tiposGarantiaBancaria.${clave}.interes`);
      if (!esFinito(o.comMinBOB)) faltantes.push(`tiposGarantiaBancaria.${clave}.comMinBOB`);
    }
  }
  const fletes = cat.tiposFlete;
  if (!esObjeto(fletes)) faltantes.push('tiposFlete');
  else {
    for (const [clave, cfg] of Object.entries(fletes)) {
      const pct = esObjeto(cfg) ? cfg.pct : undefined;
      if (pct !== null && !esFinito(pct)) faltantes.push(`tiposFlete.${clave}.pct`);
    }
  }
  const red = cat.redondeo;
  if (!esObjeto(red)) faltantes.push('redondeo');
  else {
    if (red.modo !== 'NINGUNO' && red.modo !== 'CENTAVOS') faltantes.push('redondeo.modo');
    if (!esFinito(red.decimales) || !Number.isInteger(red.decimales) || red.decimales < 0) {
      faltantes.push('redondeo.decimales');
    }
  }
  if (faltantes.length > 0) throw new CatalogoIncompletoError(faltantes);
}

/**
 * Arma el snapshot v2: entradas + reglas congeladas + resultados.
 *
 * `c` son las REGLAS DE LA COTIZACIÓN (las congeladas si ya es v2; las constantes de hoy si es una v1;
 * las de ahora si es nueva): el motor las usa para todos los ítems, también los editados, así la regla
 * de redondeo nunca se mezcla dentro de una cotización (D10). Lo que cambia por ítem es la TARIFA de
 * flete (C11), que vive en `calculos[id].tarifa`:
 *  - sin `previo`, cada ítem toma la propuesta vigente (`fleteVigente`, o la tabla informativa);
 *  - con `previo` y sin cambio de dinero, o sólo de la boleta (`tcOficial`, `tipoGarantia`,
 *    garantías), TODOS los ítems conservan su cálculo guardado y se rehacen subtotales y boleta;
 *  - un ítem agregado toma la propuesta vigente; uno EDITADO se recalcula con su tarifa guardada y
 *    sólo toma la vigente si cambió algo que la determina (tipo de compra, tipo de flete, override);
 *  - si cambió una entrada global de ítem (IVA, previsiones, retenciones) se recalculan todos, pero
 *    los ítems conservan su tarifa (una v1 la hereda de lo guardado);
 *  - `retarifar` y `recalcularTodos` son las órdenes explícitas de actualizar.
 */
export function construirSnapshot(
  params: PricerParams,
  items: PricerItem[],
  garantias: GarantiaItem[],
  c: CatalogoMotor,
  opciones?: {
    previo?: SnapshotVista;
    fleteVigente?: FleteVigente;
    /** Actualización explícita del flete: `FALTANTE` (sin override y con flete 0, si hay propuesta) o `TODOS`. */
    retarifar?: 'FALTANTE' | 'TODOS';
    /** Actualización explícita de las reglas: rehace todos los ítems (cada uno con su tarifa). */
    recalcularTodos?: boolean;
    /** El cálculo estaba pendiente de aceptar y esta escritura no lo acepta: la marca viaja con el snapshot. */
    aceptacionPendiente?: boolean;
    /** El total de antes (I17); sólo se escribe junto con la marca. */
    totalAntesDeAceptar?: number;
  }
): SnapshotV2 {
  validarCatalogo(c);
  const previo = opciones?.previo;
  const cambio = previo
    ? cambiaElDinero(
        entradasDeDinero(previo.params, previo.items, previo.garantias),
        entradasDeDinero(params, items, garantias)
      )
    : null;
  const editados = new Set<number>(cambio ? cambio.itemsCambiados : items.map((i) => i.id));
  const recalcularTodos =
    !previo || (cambio?.globalesDeItem ?? true) || opciones?.recalcularTodos === true;
  const itemsPrevios = new Map<number, PricerItem>((previo?.items ?? []).map((i) => [i.id, i]));
  // La tarifa sigue al `uid`, no a la posición (I13): si dos ítems intercambian su número, cada uno encuentra
  // su cálculo previo por `uid`. Sin `uid` en alguno de los dos lados (una v1, una pantalla vieja) se sigue por `id`.
  const itemsPreviosPorUid = new Map<string, PricerItem>(
    (previo?.items ?? []).filter((i) => !!i.uid).map((i) => [i.uid!, i])
  );

  const calculos: Record<number, ItemCalculo> = {};
  for (const it of items) {
    const itemPrevio =
      (it.uid ? itemsPreviosPorUid.get(it.uid) : undefined) ?? itemsPrevios.get(it.id);
    const guardado = itemPrevio ? previo?.calculos[itemPrevio.id] : undefined;
    // `guardado.tarifa ?? tarifaHeredada(...)` es una DEFENSA (r3, ronda T8): en producción el previo
    // pasa siempre por `leerSnapshot`, que ya completó `tarifa`; sólo un previo crudo (v1 sin leer)
    // llega acá sin ella. Lo cubre el test de previo crudo y la mutación A2c.
    const tarifaGuardada = guardado
      ? (guardado.tarifa ?? tarifaHeredada(guardado, itemPrevio))
      : undefined;
    // Retarifa (toma la vigente) sólo un ítem nuevo, uno al que se le cambió algo que determina su
    // tarifa, o uno al que se lo pidieron explícitamente. Editar cantidad, costo, etc. NO retarifa.
    const retarifa =
      !guardado ||
      !itemPrevio ||
      esOtroItem(itemPrevio, it) ||
      cambiaLaTarifa(itemPrevio, it) ||
      pideRetarifar(opciones?.retarifar, tarifaGuardada, it, c, opciones?.fleteVigente);
    if (guardado && !recalcularTodos && !editados.has(it.id) && !retarifa) {
      // Sin cambio que lo toque: se conserva exactamente (también su tarifa; una v1 la hereda).
      calculos[it.id] = { ...guardado, tarifa: tarifaGuardada! };
    } else {
      calculos[it.id] = calcularItem(it, params, c, {
        tarifa: retarifa ? undefined : tarifaGuardada,
        fleteVigente: opciones?.fleteVigente,
      });
    }
  }

  const subtotales = calcularSubtotales(items, calculos, c);
  const boletaBase = calcularBoleta(
    subtotales.totalFact,
    garantias,
    params.tipoGarantia,
    params.tcOficial,
    c
  );
  // Lo que se CARGA en el precio: con NINGUNO es el costo de la boleta; con CENTAVOS es la suma de las
  // boletas por línea, repartidas por unidad (C15).
  const totalCargado = completarBoleta(items, calculos, boletaBase, c.redondeo);
  const boletaCalc: BoletaCalculo = { ...boletaBase, totalCargado };
  const totalConBoleta = aNumero(dec(subtotales.totalFact).plus(dec(totalCargado)));

  return {
    schemaVersion: 2,
    params,
    items,
    garantias,
    calculos,
    subtotales,
    boletaCalc,
    totalConBoleta,
    catalogo: c,
    ...(opciones?.aceptacionPendiente === true ? { aceptacionPendiente: true as const } : {}),
    ...(opciones?.aceptacionPendiente === true && typeof opciones.totalAntesDeAceptar === 'number'
      ? { totalAntesDeAceptar: opciones.totalAntesDeAceptar }
      : {}),
  };
}

/**
 * Mismo producto: con número de parte, marca + número de parte (corregir la descripción no lo vuelve otro
 * producto, T5d I12-bis); sin número de parte, marca + descripción. La usa `asignarUids` (servidor).
 */
export function mismoProducto(
  a: { marcaProducto?: string; numParte?: string; descripcion?: string },
  b: { marcaProducto?: string; numParte?: string; descripcion?: string }
): boolean {
  const limpio = (x: unknown) => String(x ?? '').trim();
  return (
    limpio(a.marcaProducto) === limpio(b.marcaProducto) &&
    limpio(a.numParte) === limpio(b.numParte) &&
    (limpio(a.numParte) !== '' || limpio(a.descripcion) === limpio(b.descripcion))
  );
}

/** Mismo `id` pero otro `uid`: un producto nuevo que reusó el número del borrado (I1). Sin `uid` en alguno de los dos, se sigue por `id`. */
function esOtroItem(antes: PricerItem, despues: PricerItem): boolean {
  return !!antes.uid && !!despues.uid && antes.uid !== despues.uid;
}

/** ¿La orden explícita de actualizar el flete alcanza a este ítem? */
function pideRetarifar(
  modo: 'FALTANTE' | 'TODOS' | undefined,
  tarifa: TarifaItem | undefined,
  item: PricerItem,
  c: CatalogoMotor,
  fleteVigente?: FleteVigente
): boolean {
  if (!modo || !tarifa) return false;
  // TODOS retarifa sólo los ítems cuyo tipo de flete TIENE propuesta hoy (I7): sin ella, retarifar bajaría el
  // flete a 0 en silencio. Los demás conservan la suya.
  if (modo === 'TODOS') return propuestaDelTipo(item.tipoFlete, c, fleteVigente) != null;
  // FALTANTE: lleva flete, flete en 0 y AHORA hay propuesta. (Un override, aunque sea 0, queda MANUAL
  // igual: `tarifaDeItem` lo respeta, así que no hace falta preguntarlo acá.)
  return (
    tarifa.origenFlete !== 'NO_APLICA' &&
    tarifa.pctFlete === 0 &&
    propuestaDelTipo(item.tipoFlete, c, fleteVigente) != null
  );
}

/**
 * Rellena en cada cálculo `boleta`,`totalConBoleta`, `precioUnitarioConBoleta` y asegura `margenControlado`.
 * Devuelve lo que se carga por boleta en total.
 *
 * NINGUNO: la parte de cada línea es su reparto a prorrata (como siempre) y el total cargado es el costo de
 * la boleta; el unitario con boleta se deriva (`precioCIVA + boleta / cantidad`).
 * CENTAVOS (C15): la parte de la línea se redondea POR UNIDAD, `unit = aCentavos(exacto / cantidad)`, y la
 * línea con boleta es `aCentavos((precioCIVA + unit) × cantidad)`: así unitario × cantidad da la línea y la
 * suma de las líneas da el total. Si lo cargado en la línea se aparta más del tope de lo exacto, la línea
 * lleva su boleta exacta (`boletaPorUnidad: false`).
 */
function completarBoleta(
  items: PricerItem[],
  calculos: Record<number, ItemCalculo>,
  boletaCalc: BoletaCalculo,
  regla: ReglaRedondeo
): number {
  const reparto = repartir(items, calculos, boletaCalc.totalCosto);
  let cargado = CERO;
  for (const it of items) {
    const calc = calculos[it.id];
    if (!calc) continue;
    const exacto = dec(reparto[it.id] ?? 0);
    const precio = dec(calc.precioCIVA);
    const facturado = dec(calc.totalFacturado);
    const cant = dec(it.cantidad);
    let boleta = exacto;
    let unitario: Dec;
    let totalLinea = facturado.plus(exacto);
    delete calc.boletaPorUnidad;
    delete calc.unitarioConBoletaDerivado;
    // A2: la señal de lectura de una v1 NUNCA se guarda; una v2 (también sus líneas conservadas) no la lleva.
    delete calc.boletaRepartidaAlLeer;
    if (regla.modo === 'CENTAVOS' && cant.gt(0) && exacto.gt(0)) {
      const unit = aCentavos(exacto.div(cant));
      unitario = precio.plus(unit);
      const porUnidad = aCentavos(unitario.times(cant));
      const cargada = porUnidad.minus(facturado);
      if (cargada.minus(exacto).abs().lte(TOPE_BOLETA_POR_UNIDAD_USD)) {
        boleta = cargada;
        totalLinea = porUnidad;
        calc.boletaPorUnidad = true;
      } else {
        calc.boletaPorUnidad = false;
        // La línea lleva su boleta EXACTA. Decisión 6a, confirmada por Sebastián el 14-oct: el
        // unitario de ESTA línea va SIN la boleta y a 2 decimales (`precioCIVA`, que ya cierra con la cantidad),
        // y la boleta exacta de la línea (`boleta`) va en un renglón propio, «Costo financiero de garantía»: todo cierra al centavo. Las demás
        // líneas siguen con la boleta dentro del unitario.
        unitario = precio;
      }
    } else if (regla.modo === 'CENTAVOS') {
      unitario = precio;
    } else {
      unitario = cant.gt(0) ? precio.plus(exacto.div(cant)) : precio;
      calc.unitarioConBoletaDerivado = true;
    }
    cargado = cargado.plus(boleta);
    calc.boleta = aNumero(boleta);
    calc.totalConBoleta = aNumero(totalLinea);
    calc.precioUnitarioConBoleta = aNumero(unitario);
    if (calc.margenControlado == null) {
      calc.margenControlado = margenOperativo(calc.precioSinIVA, calc.totalPrev, calc.costoAlm);
    }
  }
  // Con NINGUNO el total cargado es el costo de la boleta tal cual (no la suma de las partes a centavos).
  return regla.modo === 'CENTAVOS' ? aNumero(cargado) : boletaCalc.totalCosto;
}

// ============================================================================
// LECTURA (v1 y v2)
// ============================================================================

const SUBTOTALES_VACIOS: Subtotales = {
  totalCompraExt: 0,
  totalCompraLoc: 0,
  totalCompra: 0,
  totalCostoAlm: 0,
  totalMkup: 0,
  totalPrev: 0,
  totalSinIVA: 0,
  totalFact: 0,
  margenGlobal: 0,
  hayCompraExterior: false,
};

const clonar = <T>(v: T): T => (v === undefined ? v : (JSON.parse(JSON.stringify(v)) as T));

/**
 * Lee un payload guardado y lo deja listo para mostrar. Decide la versión por el CONTENIDO
 * (`schemaVersion === 2`), nunca por la columna `payloadVersion`.
 *  - v2: se devuelve tal cual (copia).
 *  - v1: se COMPLETA lo que la vista necesita y v1 no trae (`margenControlado`, `boleta`,
 *    `totalConBoleta` y la `tarifa` HEREDADA de lo guardado, por ítem); no se toca ningún número
 *    guardado y no se recalcula nada.
 *  - nulo, `{}` o sin `calculos`: un snapshot vacío seguro (no lanza).
 */
export function leerSnapshot(payload: unknown): { version: 1 | 2; snapshot: SnapshotVista } {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- el payload guardado es JSON de forma abierta
  const p = (payload && typeof payload === 'object' ? payload : {}) as Record<string, any>;
  const version: 1 | 2 = p.schemaVersion === 2 ? 2 : 1;
  const items: PricerItem[] = Array.isArray(p.items) ? clonar(p.items) : [];
  const calculos: Record<number, ItemCalculo> =
    p.calculos && typeof p.calculos === 'object' ? clonar(p.calculos) : {};
  const subtotales: Subtotales =
    p.subtotales && typeof p.subtotales === 'object'
      ? clonar(p.subtotales)
      : { ...SUBTOTALES_VACIOS };
  const boletaCalc: BoletaCalculo =
    p.boletaCalc && typeof p.boletaCalc === 'object'
      ? clonar(p.boletaCalc)
      : { totalCosto: 0, pctSobreTotal: 0, detalles: [], tipoGarantia: 'Ninguno', comMinUSD: 0 };

  const snapshot: SnapshotVista = {
    params: (p.params && typeof p.params === 'object' ? clonar(p.params) : {}) as PricerParams,
    items,
    garantias: Array.isArray(p.garantias) ? clonar(p.garantias) : [],
    calculos,
    subtotales,
    boletaCalc,
    totalConBoleta: num(p.totalConBoleta),
    catalogo: version === 2 && p.catalogo ? clonar(p.catalogo) : null,
  };
  if ('pagosQuote' in p) snapshot.pagosQuote = clonar(p.pagosQuote);
  if (version === 2 && p.aceptacionPendiente === true) {
    snapshot.aceptacionPendiente = true;
    if (typeof p.totalAntesDeAceptar === 'number') {
      snapshot.totalAntesDeAceptar = p.totalAntesDeAceptar;
    }
  }

  if (version === 2) {
    // A2 (defensa): la señal sólo existe en una v1; un payload v2 que la traiga la pierde al leerlo.
    for (const calc of Object.values(calculos)) delete calc?.boletaRepartidaAlLeer;
  }
  if (version === 1) {
    // Adaptador v1 → vista. `margenControlado` sale de lo guardado, como ya hacen las reglas (no se
    // copia `margenOpAprox`, que un payload muy viejo puede no traer).
    for (const calc of Object.values(calculos)) {
      if (calc && calc.margenControlado == null) {
        calc.margenControlado = margenOperativo(calc.precioSinIVA, calc.totalPrev, calc.costoAlm);
      }
    }
    const reparto = repartir(items, calculos, num(boletaCalc.totalCosto));
    for (const it of items) {
      const calc = calculos[it.id];
      if (!calc) continue;
      if (calc.boleta == null) {
        calc.boleta = reparto[it.id] ?? 0;
        calc.boletaRepartidaAlLeer = true;
      }
      if (calc.tarifa == null) calc.tarifa = tarifaHeredada(calc, it);
      if (calc.totalConBoleta == null) {
        calc.totalConBoleta = aNumero(dec(calc.totalFacturado).plus(dec(calc.boleta)));
      }
    }
  }
  // Lo que se agregó en T5b (pedido 9 de F4): derivado y marcado cuando el payload no lo trae.
  if (boletaCalc.totalCargado == null) boletaCalc.totalCargado = num(boletaCalc.totalCosto);
  for (const it of items) {
    const calc = calculos[it.id];
    if (!calc || calc.precioUnitarioConBoleta != null) continue;
    const cant = dec(it.cantidad);
    calc.precioUnitarioConBoleta = aNumero(
      cant.gt(0) ? dec(calc.precioCIVA).plus(dec(calc.boleta).div(cant)) : dec(calc.precioCIVA)
    );
    calc.unitarioConBoletaDerivado = true;
  }
  return { version, snapshot };
}

export interface DiferenciaDeTarifa {
  itemId: number;
  num: number;
  pctFleteAntes: number;
  pctFleteDespues: number;
  totalAntes: number;
  totalDespues: number;
}

/**
 * «Actualizar tarifas…» (pedido 6 de F5): qué cambiaría si se aplicaran las reglas y la propuesta de flete
 * de HOY a una cotización guardada, SIN escribir nada. Es el mismo cálculo que la orden explícita del
 * servidor `actualizar { reglas: true, flete: 'TODOS' }` (así lo simulado es lo aplicado): la pantalla no
 * reimplementa nada.
 */
export function diferenciasConTarifasDeHoy(
  cotizacion: SnapshotVista,
  reglasDeHoy: CatalogoMotor,
  fleteVigente: FleteVigente
): { totalGuardado: number; totalConTarifasDeHoy: number; cambios: DiferenciaDeTarifa[] } {
  const hoy = construirSnapshot(
    cotizacion.params,
    cotizacion.items,
    cotizacion.garantias,
    reglasDeHoy,
    { previo: cotizacion, fleteVigente, retarifar: 'TODOS', recalcularTodos: true }
  );
  const cambios: DiferenciaDeTarifa[] = [];
  for (const it of cotizacion.items) {
    const antes = cotizacion.calculos[it.id];
    const despues = hoy.calculos[it.id];
    if (!antes || !despues) continue;
    const pctAntes = antes.tarifa?.pctFlete ?? tarifaHeredada(antes, it).pctFlete;
    if (
      pctAntes === despues.tarifa.pctFlete &&
      antes.totalConBoleta === despues.totalConBoleta &&
      antes.totalFacturado === despues.totalFacturado
    ) {
      continue;
    }
    cambios.push({
      itemId: it.id,
      num: it.num,
      pctFleteAntes: pctAntes,
      pctFleteDespues: despues.tarifa.pctFlete,
      totalAntes: antes.totalConBoleta,
      totalDespues: despues.totalConBoleta,
    });
  }
  return {
    totalGuardado: cotizacion.totalConBoleta,
    totalConTarifasDeHoy: hoy.totalConBoleta,
    cambios,
  };
}

// ============================================================================
// ENMIENDA DE COSTOS CON EL PRECIO FIJO (F6 T18)
// ============================================================================

/** Lo único que una enmienda puede mover: los costos de una línea. Todo lo demás de la línea queda como está. */
export interface CambioDeCostos {
  itemId: number;
  costoUnitario?: number;
  pctDescuento?: number;
  tipoFlete?: string;
  pctFleteOverride?: number | null;
  pctNacionalizacion?: number;
}

export type MotivoDeEnmiendaImposible = 'MARKUP_FUERA_DE_RANGO' | 'FLETE_SIN_PORCENTAJE';

export class EnmiendaImposibleError extends Error {
  constructor(
    readonly motivo: MotivoDeEnmiendaImposible,
    readonly itemId: number,
    mensaje: string
  ) {
    super(mensaje);
    this.name = 'EnmiendaImposibleError';
  }
}

/**
 * El markup (%) que deja la línea EXACTAMENTE en el precio guardado con los costos de `item`: el precio al cliente no
 * se mueve y lo que se deriva es el markup (F6 T18, definición de Sebastián). La fórmula de `calcularItemBase`
 * dado el precio:
 *   precioConMarkup = costoAlm / (1 − markup)  =>  markup = 1 − costoAlm / precioConMarkup
 * `precioConMarkup` es el GUARDADO (las previsiones son un % de él, así que con el precio fijo no cambian).
 * Devuelve también el cálculo de la línea: todo lo que depende del precio queda BYTE A BYTE como estaba; sólo se
 * rehacen los costos y lo que se mide contra ellos (markup, márgenes, totales de costo). Es la única función del
 * motor que parte de un precio y no de un markup, y la única excepción del guardián 03 (nadie recalcula lo guardado).
 */
export function markupParaPrecio(
  item: PricerItem,
  params: PricerParams,
  c: CatalogoMotor,
  guardado: ItemCalculo,
  tarifa: TarifaItem
): { markup: number; calculo: ItemCalculo } {
  const base = calcularItemBase(item, params, c, tarifa);
  const precioConMarkup = dec(guardado.precioConMarkup);
  const costoAlm = dec(base.costoAlm);
  if (!precioConMarkup.gt(0) || costoAlm.lte(0)) {
    throw new EnmiendaImposibleError(
      'MARKUP_FUERA_DE_RANGO',
      item.id,
      `La línea #${String(item.num).padStart(2, '0')} no tiene un precio o un costo con el que derivar el markup: pedí la reapertura.`
    );
  }
  const markup = UNO.minus(costoAlm.div(precioConMarkup)).times(100);
  if (markup.gte(100)) {
    throw new EnmiendaImposibleError(
      'MARKUP_FUERA_DE_RANGO',
      item.id,
      `El markup de la línea #${String(item.num).padStart(2, '0')} queda fuera de rango: pedí la reapertura.`
    );
  }
  const cant = dec(item.cantidad);
  const montoMarkup = precioConMarkup.minus(costoAlm);
  const precioSinIVA = dec(guardado.precioSinIVA);
  const totalPrev = dec(guardado.totalPrev);
  const baseOp = precioSinIVA.minus(totalPrev);
  const margenEfectivo = precioSinIVA.gt(0) ? precioSinIVA.minus(costoAlm).div(precioSinIVA) : CERO;
  const margenOpAprox = baseOp.gt(0) ? baseOp.minus(costoAlm).div(baseOp) : CERO;
  return {
    markup: aNumero(markup),
    calculo: {
      ...guardado,
      costoCDesc: base.costoCDesc,
      montoFlete: base.montoFlete,
      cif: base.cif,
      montoNac: base.montoNac,
      montoRetencion: base.montoRetencion,
      costoAlm: base.costoAlm,
      montoMarkup: aNumero(montoMarkup),
      totalCostoAlm: aNumero(costoAlm.times(cant)),
      totalMarkup: aNumero(montoMarkup.times(cant)),
      margenEfectivo: aNumero(margenEfectivo),
      margenOpAprox: aNumero(margenOpAprox),
      margenControlado: aNumero(margenOpAprox),
      tarifa,
    },
  };
}

/** Lo que cambió en una línea al enmendar: el markup y los costos, antes y después (el precio no aparece: no cambia). */
export interface LineaEnmendada {
  itemId: number;
  numero: number;
  markupAntes: number;
  markupDespues: number;
  costoAlmAntes: number;
  costoAlmDespues: number;
}

/** El markup (%) de una línea guardada, a partir de su precio y su costo: (precioConMarkup − costoAlm) / precioConMarkup. */
function markupDeCalculo(calc: ItemCalculo): Dec {
  const p = dec(calc.precioConMarkup);
  return p.gt(0) ? p.minus(dec(calc.costoAlm)).div(p).times(100) : CERO;
}

/**
 * Aplica una enmienda de costos a un snapshot v2 CONSERVANDO el precio: `precioSinIVA`, `precioCIVA`, `totalFacturado`,
 * la boleta, `totalConBoleta` y los totales de precio de la cotización quedan idénticos; cambian los costos, el markup
 * de las líneas tocadas y lo que se deriva de ellos (márgenes y totales de costo). Usa el catálogo CONGELADO en el
 * propio snapshot, nunca el de hoy. Si una línea no se puede enmendar (markup fuera de rango, flete sin porcentaje) lanza
 * `EnmiendaImposibleError` y no devuelve nada.
 */
export function enmendarCostos(
  snap: SnapshotV2,
  cambios: CambioDeCostos[]
): { snapshot: SnapshotV2; lineas: LineaEnmendada[] } {
  const c = snap.catalogo;
  const porId = new Map(cambios.map((x) => [x.itemId, x]));
  const lineas: LineaEnmendada[] = [];
  const calculos: Record<number, ItemCalculo> = { ...snap.calculos };
  const items = snap.items.map((it) => {
    const cambio = porId.get(it.id);
    if (!cambio) return it;
    const nuevo: PricerItem = {
      ...it,
      ...(cambio.costoUnitario !== undefined ? { costoUnitario: cambio.costoUnitario } : {}),
      ...(cambio.pctDescuento !== undefined ? { pctDescuento: cambio.pctDescuento } : {}),
      ...(cambio.tipoFlete !== undefined ? { tipoFlete: cambio.tipoFlete } : {}),
      ...(cambio.pctFleteOverride !== undefined
        ? { pctFleteOverride: cambio.pctFleteOverride }
        : {}),
      ...(cambio.pctNacionalizacion !== undefined
        ? { pctNacionalizacion: cambio.pctNacionalizacion }
        : {}),
    };
    const guardado = snap.calculos[it.id]!;
    const tarifaGuardada = guardado.tarifa ?? tarifaHeredada(guardado, it);
    const cambiaFlete =
      (cambio.tipoFlete !== undefined && cambio.tipoFlete !== it.tipoFlete) ||
      (cambio.pctFleteOverride !== undefined &&
        (cambio.pctFleteOverride ?? null) !== (it.pctFleteOverride ?? null));
    const tarifa = cambiaFlete ? tarifaDeItem(nuevo, c) : tarifaGuardada;
    if (tarifa.origenFlete === 'SIN_PROPUESTA') {
      throw new EnmiendaImposibleError(
        'FLETE_SIN_PORCENTAJE',
        it.id,
        `La línea #${String(it.num).padStart(2, '0')} lleva flete y no tiene un porcentaje: escribilo.`
      );
    }
    const { markup, calculo } = markupParaPrecio(nuevo, snap.params, c, guardado, tarifa);
    calculos[it.id] = calculo;
    lineas.push({
      itemId: it.id,
      numero: it.num,
      markupAntes: aNumero(markupDeCalculo(guardado)),
      markupDespues: markup,
      costoAlmAntes: guardado.costoAlm,
      costoAlmDespues: calculo.costoAlm,
    });
    return { ...nuevo, markup };
  });
  const derivados = calcularSubtotales(items, calculos, c);
  // El precio no se recalcula: los totales de precio son los guardados, tal cual.
  const subtotales: Subtotales = {
    ...derivados,
    totalPrev: snap.subtotales.totalPrev,
    totalSinIVA: snap.subtotales.totalSinIVA,
    totalFact: snap.subtotales.totalFact,
  };
  return {
    snapshot: { ...snap, items, calculos, subtotales },
    lineas,
  };
}

export { REGLA_NINGUNA };
